import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  api,
  ApiError,
  type CategoryNode,
  type ProductCard,
  type ProductImage,
} from '../api/client'
import ValueSetInput from '../components/ValueSetInput'
import { useAuth } from '../auth/AuthProvider'
import { Button, ConfirmDialog, Field, Input, Notice, Select, Spinner } from '../ui/controls'
import { imageUrl } from '../ui/images'

/**
 * Карточка товара (п. 4.7).
 *
 * Блоки: основные сведения, атрибуты, изображения, остатки. Блок атрибутов
 * строится по набору категории с учётом наследования (А-602).
 */
export default function ProductCardPage() {
  const { id } = useParams()
  const nav = useNavigate()
  const { state } = useAuth()
  const canWrite = state.status === 'authenticated' && state.me.permissions.write.catalog
  const canPrice = state.status === 'authenticated' && state.me.permissions.write.pricing

  const isNew = id === 'new'
  const [card, setCard] = useState<ProductCard | null>(null)
  const [categories, setCategories] = useState<CategoryNode[]>([])
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)

  // Черновик формы: правки не уходят на сервер до нажатия «Сохранить».
  const [form, setForm] = useState({
    sku: '',
    name_ru: '',
    name_kk: '',
    description_ru: '',
    brand: '',
    barcode: '',
    price: '0',
    unit: 'шт',
    slug: '',
    category_id: 0,
  })
  const [values, setValues] = useState<Record<string, string[]>>({})
  const [dirty, setDirty] = useState(false)
  const [pending, setPending] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const load = useCallback(async (pid: number) => {
    const c = await api.product(pid)
    setCard(c)
    setForm({
      sku: c.sku,
      name_ru: c.name_ru,
      name_kk: c.name_kk ?? '',
      description_ru: c.description_ru ?? '',
      brand: c.brand ?? '',
      barcode: c.barcode ?? '',
      price: String(c.price),
      unit: c.unit,
      slug: c.slug,
      category_id: c.category_id,
    })
    setValues(Object.fromEntries(c.attributes.map((a) => [a.code, a.values])))
    setDirty(false)
  }, [])

  useEffect(() => {
    void api.categoryTree().then((t) => {
      const list = flat(t)
      setCategories(list)
      if (isNew && list.length > 0) {
        setForm((f) => (f.category_id === 0 ? { ...f, category_id: list[0].id } : f))
      }
    })
  }, [isNew])

  useEffect(() => {
    if (isNew || !id) return
    void load(Number(id)).catch((e) =>
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось загрузить' }),
    )
  }, [id, isNew, load])

  // Предупреждение при уходе со страницы с несохранёнными правками (А-611).
  useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  function set<K extends keyof typeof form>(key: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: v }))
    setDirty(true)
  }

  async function save() {
    setPending(true)
    setNotice(null)
    try {
      const body = {
        sku: form.sku.trim(),
        category_id: form.category_id,
        name_ru: form.name_ru.trim(),
        name_kk: form.name_kk.trim() || null,
        description_ru: form.description_ru.trim() || null,
        brand: form.brand.trim() || null,
        barcode: form.barcode.trim() || null,
        price: Number(form.price) || 0,
        unit: form.unit,
        slug: form.slug.trim(),
        attributes: values,
      }
      if (isNew) {
        const created = await api.createProduct(body)
        setDirty(false)
        nav(`/products/${created.id}`, { replace: true })
      } else {
        const updated = await api.updateProduct(Number(id), body)
        setCard(updated)
        setValues(Object.fromEntries(updated.attributes.map((a) => [a.code, a.values])))
        setDirty(false)
        setNotice({ kind: 'success', text: 'Сохранено' })
      }
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось сохранить' })
    } finally {
      setPending(false)
    }
  }

  if (!isNew && card === null) return <Spinner label="Загрузка карточки…" />

  const readiness = card?.readiness

  return (
    <section className="max-w-5xl">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => nav('/products')}
            className="mb-1 text-xs text-ink-500 hover:text-brand-600"
          >
            ← К списку товаров
          </button>
          <h2 className="truncate text-lg font-semibold text-ink-900 dark:text-ink-50">
            {isNew ? 'Новый товар' : card?.name_ru}
          </h2>
          {card && (
            <p className="mt-0.5 text-sm text-ink-500">
              Артикул {card.sku} · изменён {card.updated_at}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {dirty && <span className="text-xs text-warning-700 dark:text-warning-100">Есть несохранённые изменения</span>}
          {canWrite && (
            <Button variant="primary" onClick={save} disabled={pending || form.name_ru.trim() === ''}>
              {pending ? 'Сохранение…' : isNew ? 'Создать' : 'Сохранить'}
            </Button>
          )}
          {!isNew && canWrite && (
            <Button variant="danger" onClick={() => setConfirmDelete(true)} disabled={pending}>
              Удалить
            </Button>
          )}
        </div>
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      {/* Проверка перед публикацией и перечень незаполненного (А-605, А-606) */}
      {card && !isNew && (
        <PublishBlock
          card={card}
          canPrice={canPrice}
          onChanged={async () => {
            await load(card.id)
            setNotice({ kind: 'success', text: 'Состояние публикации изменено' })
          }}
          onError={(t) => setNotice({ kind: 'error', text: t })}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]">
        <div className="space-y-6">
          <Block title="Основные сведения">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Артикул">
                <Input
                  value={form.sku}
                  onChange={(e) => set('sku', e.target.value)}
                  disabled={!canWrite || pending}
                  className="font-mono"
                />
              </Field>
              <Field label="Штрихкод" hint="Необязательно">
                <Input
                  value={form.barcode}
                  onChange={(e) => set('barcode', e.target.value)}
                  disabled={!canWrite || pending}
                  className="font-mono"
                />
              </Field>
            </div>

            <Field label="Название (рус)">
              <Input
                value={form.name_ru}
                onChange={(e) => {
                  set('name_ru', e.target.value)
                  // Адресный сегмент предлагается по названию, пока его
                  // не правили вручную (А-608).
                  if (isNew) set('slug', translit(e.target.value))
                }}
                disabled={!canWrite || pending}
                required
              />
            </Field>

            <Field label="Название (каз)" hint="Необязательно">
              <Input
                value={form.name_kk}
                onChange={(e) => set('name_kk', e.target.value)}
                disabled={!canWrite || pending}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Бренд">
                <Input
                  value={form.brand}
                  onChange={(e) => set('brand', e.target.value)}
                  disabled={!canWrite || pending}
                />
              </Field>
              <Field
                label="Цена, ₸"
                hint={!canPrice ? 'Изменение цены недоступно вашей роли' : undefined}
              >
                <Input
                  type="number"
                  min={0}
                  value={form.price}
                  onChange={(e) => set('price', e.target.value)}
                  disabled={!canPrice || pending}
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Единица измерения">
                <Input
                  value={form.unit}
                  onChange={(e) => set('unit', e.target.value)}
                  disabled={!canWrite || pending}
                />
              </Field>
              <Field label="Адресный сегмент" hint="Участвует в адресе товара на витрине">
                <Input
                  value={form.slug}
                  onChange={(e) => set('slug', e.target.value)}
                  disabled={!canWrite || pending}
                  className="font-mono"
                />
              </Field>
            </div>

            <Field label="Описание">
              <textarea
                value={form.description_ru}
                onChange={(e) => set('description_ru', e.target.value)}
                disabled={!canWrite || pending}
                rows={4}
                className="w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 shadow-xs focus:border-brand-500 disabled:opacity-60 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
              />
            </Field>

            {isNew ? (
              <Field label="Категория">
                <Select value={form.category_id} onChange={(e) => set('category_id', Number(e.target.value))}
                  className="w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {' '.repeat((c.depth - 1) * 3)}
                      {c.name_ru}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : (
              card && (
                <CategoryChanger
                  card={card}
                  categories={categories}
                  canWrite={canWrite}
                  onChanged={async (updated) => {
                    setCard(updated)
                    setValues(Object.fromEntries(updated.attributes.map((a) => [a.code, a.values])))
                    setForm((f) => ({ ...f, category_id: updated.category_id }))
                    setNotice({ kind: 'success', text: 'Категория изменена' })
                  }}
                  onError={(t) => setNotice({ kind: 'error', text: t })}
                />
              )
            )}
          </Block>

          {card && (
            <Block title="Атрибуты">
              {card.attributes.length === 0 ? (
                <p className="text-sm text-ink-500">
                  У категории нет привязанных атрибутов. Настройте их набор в разделе
                  «Категории» — они появятся здесь автоматически.
                </p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {card.attributes.map((a) => (
                    <Field
                      key={a.code}
                      label={a.name_ru + (a.unit ? `, ${a.unit}` : '')}
                      hint={a.inherited ? `из категории «${a.owner_category_name}»` : undefined}
                    >
                      <div className="relative">
                        {a.value_type === 'multilist' ? (
                          <ValueSetInput
                            id={`attribute:${a.code}`}
                            values={values[a.code] ?? []}
                            onChange={(next) => {
                              setValues((v) => ({ ...v, [a.code]: next }))
                              setDirty(true)
                            }}
                            disabled={!canWrite || pending}
                            invalid={a.is_required && (values[a.code] ?? []).length === 0}
                          />
                        ) : (
                          <Input
                            id={`attribute:${a.code}`}
                            value={values[a.code]?.[0] ?? ''}
                            onChange={(e) => {
                              setValues((v) => ({ ...v, [a.code]: [e.target.value] }))
                              setDirty(true)
                            }}
                            disabled={!canWrite || pending}
                            className={
                              a.is_required && !(values[a.code]?.[0] ?? '').trim()
                                ? 'border-warning-500'
                                : ''
                            }
                          />
                        )}
                        {a.is_required && (
                          <span className="absolute -top-6 right-0 text-xs text-warning-700 dark:text-warning-100">
                            обязательный
                          </span>
                        )}
                      </div>
                    </Field>
                  ))}
                </div>
              )}

              {/* Сохранённые неиспользуемые значения (А-604) */}
              {card.orphaned.length > 0 && (
                <details className="mt-4 rounded-md border border-ink-200 bg-ink-50 p-3 dark:border-ink-800 dark:bg-ink-900">
                  <summary className="cursor-pointer text-sm text-ink-600 dark:text-ink-400">
                    Сохранённые значения от прежней категории ({card.orphaned.length})
                  </summary>
                  <ul className="mt-2 space-y-1 text-sm text-ink-500">
                    {card.orphaned.map((o) => (
                      <li key={o.attribute_id}>
                        {o.name_ru}: {o.value}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-ink-400">
                    Значения не участвуют в проверке готовности и вернутся в работу, если товар
                    снова попадёт в категорию с этими атрибутами.
                  </p>
                </details>
              )}
            </Block>
          )}

          {card && !isNew && (
            <Block title="Изображения">
              <ImagesBlock
                productId={card.id}
                images={card.images}
                canWrite={canWrite}
                onChanged={() => load(card.id)}
                onError={(t) => setNotice({ kind: 'error', text: t })}
              />
            </Block>
          )}
        </div>

        <div className="space-y-6">
          {card && !isNew && (
            <Block title="Остатки">
              {card.stock.length === 0 ? (
                <p className="text-sm text-ink-500">
                  Складов пока нет. Заведите их в разделе «Склады».
                </p>
              ) : (
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
                    {card.stock.map((s) => (
                      <tr key={s.warehouse_id}>
                        <td className="py-1.5 text-ink-700 dark:text-ink-300">
                          {s.warehouse_name}
                          {!s.is_public && (
                            <span className="ml-1 text-xs text-ink-400">не на витрине</span>
                          )}
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-ink-800 dark:text-ink-200">
                          {s.quantity}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-ink-300 dark:border-ink-700">
                      <td className="py-1.5 font-medium text-ink-900 dark:text-ink-50">Итого</td>
                      <td className="py-1.5 text-right font-medium tabular-nums text-ink-900 dark:text-ink-50">
                        {card.stock.reduce((s, r) => s + r.quantity, 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </Block>
          )}

          {readiness && !isNew && (
            <Block title="Готовность к публикации">
              {readiness.ready ? (
                <p className="text-sm text-success-700 dark:text-success-100">Все условия выполнены</p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {(readiness.blockers ?? []).map((b) => (
                    <li key={b.field}>
                      <button
                        type="button"
                        onClick={() => {
                          document.getElementById(b.field)?.focus()
                          document
                            .getElementById(b.field)
                            ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
                        }}
                        className="text-left text-warning-700 dark:text-warning-100 hover:underline"
                      >
                        {b.message}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Block>
          )}
        </div>
      </div>

      {confirmDelete && card && (
        <ConfirmDialog
          title="Удалить товар?"
          message={`Товар «${card.name_ru}» будет удалён вместе со значениями атрибутов и изображениями.`}
          confirmLabel="Удалить"
          pending={pending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            setPending(true)
            try {
              await api.deleteProduct(card.id)
              nav('/products')
            } catch (e) {
              setConfirmDelete(false)
              setNotice({
                kind: 'error',
                text: e instanceof ApiError ? e.message : 'Не удалось удалить',
              })
            } finally {
              setPending(false)
            }
          }}
        />
      )}
    </section>
  )
}

// --------------------------------------------------------------------------

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-ink-200 p-4 dark:border-ink-800">
      <h3 className="mb-4 text-sm font-semibold text-ink-900 dark:text-ink-50">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  )
}

function PublishBlock({
  card,
  canPrice,
  onChanged,
  onError,
}: {
  card: ProductCard
  canPrice: boolean
  onChanged: () => Promise<void>
  onError: (t: string) => void
}) {
  const [pending, setPending] = useState(false)

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-ink-200 px-4 py-3 dark:border-ink-800">
      <span
        className={`rounded-full px-2.5 py-1 text-xs font-medium ${
          card.is_published
            ? 'bg-success-100 dark:bg-success-700/25 text-success-700 dark:text-success-100'
            : 'bg-ink-100 text-ink-600 dark:bg-ink-800 dark:text-ink-400'
        }`}
      >
        {card.is_published ? 'В продаже' : 'Черновик'}
      </span>

      {!card.is_published && !card.readiness.ready && (
        <span className="text-sm text-warning-700 dark:text-warning-100">
          Не готов к публикации: {(card.readiness.blockers ?? []).length} замечаний
        </span>
      )}

      {canPrice && (
        <Button
          size="sm"
          variant={card.is_published ? 'secondary' : 'primary'}
          disabled={pending || (!card.is_published && !card.readiness.ready)}
          onClick={async () => {
            setPending(true)
            try {
              await api.setPublished(card.id, !card.is_published)
              await onChanged()
            } catch (e) {
              onError(e instanceof ApiError ? e.message : 'Не удалось изменить')
            } finally {
              setPending(false)
            }
          }}
          className="ml-auto"
        >
          {card.is_published ? 'Снять с продажи' : 'В продажу'}
        </Button>
      )}

      <span className="text-xs text-ink-400">
        Флаг публикации с остатком не связан: товар без остатка остаётся на витрине
        с пометкой об отсутствии.
      </span>
    </div>
  )
}

/** Смена категории с решением о судьбе значений атрибутов (А-603). */
function CategoryChanger({
  card,
  categories,
  canWrite,
  onChanged,
  onError,
}: {
  card: ProductCard
  categories: CategoryNode[]
  canWrite: boolean
  onChanged: (c: ProductCard) => void
  onError: (t: string) => void
}) {
  const [target, setTarget] = useState<number | ''>('')
  const [dropped, setDropped] = useState<{ attribute_id: number; name_ru: string; value: string }[] | null>(null)
  const [pending, setPending] = useState(false)

  const current = categories.find((c) => c.id === card.category_id)

  async function apply(keep: boolean) {
    setPending(true)
    try {
      onChanged(await api.changeProductCategory(card.id, Number(target), keep))
      setTarget('')
      setDropped(null)
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Не удалось сменить категорию')
    } finally {
      setPending(false)
    }
  }

  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium text-ink-700 dark:text-ink-300">
        Категория
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-ink-100 px-2.5 py-1.5 text-sm text-ink-700 dark:bg-ink-800 dark:text-ink-300">
          {current?.name_ru ?? '—'}
        </span>
        {canWrite && (
          <>
            <Select value={target} onChange={(e) => setTarget(e.target.value ? Number(e.target.value) : '')}
              className="rounded-md border border-ink-300 bg-white px-2.5 py-1.5 text-sm dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
            >
              <option value="">— перенести в —</option>
              {categories
                .filter((c) => c.id !== card.category_id)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {' '.repeat((c.depth - 1) * 3)}
                    {c.name_ru}
                  </option>
                ))}
            </Select>
            <Button
              size="sm"
              disabled={target === '' || pending}
              onClick={async () => {
                const res = await api.previewCategoryChange(card.id, Number(target))
                if (res.dropped.length === 0) {
                  await apply(false)
                } else {
                  setDropped(res.dropped)
                }
              }}
            >
              Перенести
            </Button>
          </>
        )}
      </div>

      {dropped && (
        <div className="mt-3 rounded-md border border-warning-500/40 bg-warning-100 dark:bg-warning-700/25 p-3 text-sm text-warning-700 dark:text-warning-100">
          <p className="font-medium">
            В новой категории нет этих атрибутов, значения потеряют смысл:
          </p>
          <ul className="mt-1.5 list-inside list-disc">
            {dropped.map((d) => (
              <li key={d.attribute_id}>
                {d.name_ru}: {d.value}
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="primary" disabled={pending} onClick={() => apply(true)}>
              Сохранить как неиспользуемые
            </Button>
            <Button size="sm" variant="danger" disabled={pending} onClick={() => apply(false)}>
              Удалить значения
            </Button>
            <Button size="sm" disabled={pending} onClick={() => setDropped(null)}>
              Отмена
            </Button>
          </div>
          <p className="mt-2 text-xs">
            Сохранённые значения вернутся в работу, если товар снова попадёт в категорию
            с этими атрибутами.
          </p>
        </div>
      )}
    </div>
  )
}

/** Загрузка и порядок изображений (п. 4.8). */
function ImagesBlock({
  productId,
  images,
  canWrite,
  onChanged,
  onError,
}: {
  productId: number
  images: ProductImage[]
  canWrite: boolean
  onChanged: () => Promise<void>
  onError: (t: string) => void
}) {
  const [uploading, setUploading] = useState(false)
  const [over, setOver] = useState(false)
  const [dragId, setDragId] = useState<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  async function upload(files: FileList | File[]) {
    const list = [...files]
    if (list.length === 0) return
    setUploading(true)
    try {
      const res = await api.uploadImages(productId, list)
      if (res.failed?.length) {
        onError(res.failed.map((f) => `${f.name}: ${f.error}`).join('; '))
      }
      await onChanged()
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Не удалось загрузить')
    } finally {
      setUploading(false)
    }
  }

  async function onDropImage(targetId: number) {
    if (dragId === null || dragId === targetId) return
    const order = images.map((i) => i.id)
    const from = order.indexOf(dragId)
    const to = order.indexOf(targetId)
    order.splice(to, 0, ...order.splice(from, 1))
    setDragId(null)
    await api.reorderImages(productId, order)
    await onChanged()
  }

  return (
    <div>
      {images.length > 0 && (
        <ul className="mb-4 flex flex-wrap gap-3">
          {images.map((img, i) => (
            <li
              key={img.id}
              draggable={canWrite}
              onDragStart={() => setDragId(img.id)}
              onDragOver={(e: DragEvent) => e.preventDefault()}
              onDrop={() => onDropImage(img.id)}
              className="group relative"
            >
              <img
                src={imageUrl(img.file_key, 'thumb')}
                alt={img.alt_text ?? ''}
                className="size-24 rounded-md border border-ink-200 object-cover dark:border-ink-800"
              />
              {i === 0 && (
                <span className="absolute left-1 top-1 rounded bg-brand-600 px-1.5 py-0.5 text-2xs text-white">
                  главное
                </span>
              )}
              {canWrite && (
                <button
                  type="button"
                  aria-label="Удалить изображение"
                  onClick={async () => {
                    await api.deleteImage(img.id)
                    await onChanged()
                  }}
                  className="absolute right-1 top-1 hidden rounded bg-danger-500 px-1.5 py-0.5 text-2xs text-white group-hover:block"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canWrite && (
        <div
          onDragOver={(e) => {
            e.preventDefault()
            setOver(true)
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setOver(false)
            void upload(e.dataTransfer.files)
          }}
          className={`rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
            over ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/15' : 'border-ink-300 dark:border-ink-700'
          }`}
        >
          <p className="text-sm text-ink-600 dark:text-ink-400">
            {uploading ? 'Загрузка…' : 'Перетащите изображения сюда'}
          </p>
          <p className="mt-1 text-xs text-ink-400">
            JPEG, PNG или WebP, до 10 МБ. Первое изображение становится главным — порядок
            меняется перетаскиванием.
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              if (e.target.files) void upload(e.target.files)
              e.target.value = ''
            }}
          />
          <Button
            size="sm"
            className="mt-3"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            Выбрать файлы
          </Button>
        </div>
      )}
    </div>
  )
}

// --------------------------------------------------------------------------

function flat(nodes: CategoryNode[]): CategoryNode[] {
  const out: CategoryNode[] = []
  for (const n of nodes) {
    out.push(n)
    if (n.children?.length) out.push(...flat(n.children))
  }
  return out
}

const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  ә: 'a', ғ: 'g', қ: 'k', ң: 'n', ө: 'o', ұ: 'u', ү: 'u', һ: 'h', і: 'i',
}

/**
 * Предложение адресного сегмента по названию (А-608).
 *
 * Повторяет правило серверной части, чтобы поле заполнялось по мере ввода
 * без обращения к серверу; окончательную проверку выполняет серверная часть.
 */
function translit(s: string): string {
  let out = ''
  for (const ch of s.toLowerCase()) {
    if (/[a-z0-9]/.test(ch)) out += ch
    else if (TRANSLIT[ch] !== undefined) out += TRANSLIT[ch]
    else if (/[\s\-_/,.]/.test(ch) && out && !out.endsWith('-')) out += '-'
  }
  return out.replace(/^-+|-+$/g, '').slice(0, 60)
}
